/**
 * Mapira POST /dashboard/overview/search body u view model za DashboardView.
 */

import { PROPOSALS_STATUS_OPTIONS } from '@/features/public/home/constants/homeConstants';
import { extractUnreadNotificationsFromOverview } from '@/shared/notifications/notificationNormalize';

/** Боје за dashboard графиконе — видљиве, умерено живе (није неон). */
const NEUTRAL = { color: '#94a3b8', icon: 'fa-circle' };

/** Боје трака „предлози по статусу” (не мења PROPOSALS_STATUS_OPTIONS на почетној). */
const DASHBOARD_PROPOSAL_BAR_COLOR = {
  DRAFT: '#64748b',
  CREATED: '#3d6bb5',
  UNDER_REVIEW: '#f59e0b',
  ACCEPTED: '#10b981',
  PARTIALLY_ACCEPTED: '#f97316',
  REJECTED: '#ef4444',
  REJECTED_AFTER_REVIEW: '#dc2626',
  ARCHIVED: '#78909c',
};

const PROPOSAL_STATUS_ICONS = {
  DRAFT: 'fa-file-alt',
  CREATED: 'fa-file',
  UNDER_REVIEW: 'fa-eye',
  ACCEPTED: 'fa-check-circle',
  PARTIALLY_ACCEPTED: 'fa-adjust',
  REJECTED: 'fa-times-circle',
  REJECTED_AFTER_REVIEW: 'fa-times-circle',
  ARCHIVED: 'fa-archive',
};

/** Статуси који нису у PROPOSALS_STATUS_OPTIONS (јавни филтери), али долазе са overview API-ја. */
const PROPOSAL_STATUS_EXTRA = {
  DRAFT: { label: 'Нацрт', color: '#64748b' },
  ARCHIVED: { label: 'Архивирано', color: '#78909c' },
};

/** Редослед приказа у картици „Предлози обавеза по статусу“. */
const PROPOSAL_CHART_ORDER = [
  'DRAFT',
  'CREATED',
  'UNDER_REVIEW',
  'ACCEPTED',
  'PARTIALLY_ACCEPTED',
  'REJECTED',
  'REJECTED_AFTER_REVIEW',
  'ARCHIVED',
];

const IMPL_STATUS_DISPLAY = {
  COMPLETED: { label: 'Завршено', color: '#10b981', icon: 'fa-check-circle' },
  IN_FULL: { label: 'У потпуности', color: '#22c55e', icon: 'fa-check-double' },
  SIGNIFICANT_PROGRESS: { label: 'Значајан напредак', color: '#14b8a6', icon: 'fa-chart-line' },
  LIMITED_PROGRESS: { label: 'Ограничен напредак', color: '#fbbf24', icon: 'fa-chart-bar' },
  NOT_STARTED: { label: 'Није започето', color: '#94a3b8', icon: 'fa-circle' },
  IN_PROGRESS: { label: 'У току', color: '#3b82f6', icon: 'fa-spinner' },
  MAJOR_DELAY: { label: 'Велико кашњење', color: '#ef4444', icon: 'fa-exclamation-triangle' },
  SIGNIFICANT_DELAY: { label: 'Значајно кашњење', color: '#fb923c', icon: 'fa-exclamation-circle' },
};

const IMPL_CHART_ORDER = [
  'NOT_STARTED',
  'LIMITED_PROGRESS',
  'SIGNIFICANT_PROGRESS',
  'IN_FULL',
  'IN_PROGRESS',
  'COMPLETED',
  'MAJOR_DELAY',
  'SIGNIFICANT_DELAY',
];

function normStatus(s) {
  return String(s ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
}

function proposalRowFromApi(statusRaw, count) {
  const key = normStatus(statusRaw);
  const opt = PROPOSALS_STATUS_OPTIONS.find((o) => o.value === key);
  const extra = PROPOSAL_STATUS_EXTRA[key];
  const label = opt?.label ?? extra?.label ?? String(statusRaw ?? '—').replace(/_/g, ' ');
  const color =
    DASHBOARD_PROPOSAL_BAR_COLOR[key] ?? opt?.color ?? extra?.color ?? NEUTRAL.color;
  const icon = PROPOSAL_STATUS_ICONS[key] ?? NEUTRAL.icon;
  return { key: key || `row-${label}`, label, value: Number(count) || 0, color, icon };
}

function sortObligationProposalChartRows(rows) {
  return [...rows].sort((a, b) => {
    const ia = PROPOSAL_CHART_ORDER.indexOf(a.key);
    const ib = PROPOSAL_CHART_ORDER.indexOf(b.key);
    const sa = ia === -1 ? 999 : ia;
    const sb = ib === -1 ? 999 : ib;
    if (sa !== sb) return sa - sb;
    return b.value - a.value;
  });
}

function implementationRowFromApi(statusRaw, count) {
  const key = normStatus(statusRaw);
  const preset = IMPL_STATUS_DISPLAY[key];
  const label = preset?.label ?? String(statusRaw ?? '—').replace(/_/g, ' ');
  const color = preset?.color ?? NEUTRAL.color;
  const icon = preset?.icon ?? NEUTRAL.icon;
  return { key: key || `impl-${label}`, label, value: Number(count) || 0, color, icon };
}

function sortImplementationChartRows(rows) {
  return [...rows].sort((a, b) => {
    const ia = IMPL_CHART_ORDER.indexOf(a.key);
    const ib = IMPL_CHART_ORDER.indexOf(b.key);
    const sa = ia === -1 ? 999 : ia;
    const sb = ib === -1 ? 999 : ib;
    if (sa !== sb) return sa - sb;
    return b.value - a.value;
  });
}

function coerceStatBlock(block, rowMapper) {
  const items = Array.isArray(block?.items) ? block.items : [];
  const mapped = items
    .map((it) => rowMapper(it?.status, it?.count))
    .filter((r) => r.label);
  const totalFromApi = Number(block?.total);
  const total =
    Number.isFinite(totalFromApi) && totalFromApi >= 0
      ? totalFromApi
      : mapped.reduce((s, r) => s + r.value, 0);
  return { items: mapped, total };
}

/**
 * Transformiše overview API odgovor u brojke i chart podatke za dashboard.
 * @param {object} apiBody - Parsirani JSON (axios response.data).
 * @param {{ organizations?: object[], organizationNameById?: Record<string, string> }} [options] - Opcioni lookup za imena.
 * @returns {{ totalUsers: number, moderatorsCount: number, activePosts: number, upcomingPosts: number, ocdCount: number, institutionsCount: number, proposalsByStatus: object[], proposalsTotal: number, implementationByStatus: object[], implementationTotal: number, unreadNotifications: object[], unreadNotificationsTotal: number }}
 */
export function mapDashboardOverviewToViewModel(apiBody, options = {}) {
  const root = apiBody && typeof apiBody === 'object' ? apiBody : {};
  const s = root.summary && typeof root.summary === 'object' ? root.summary : {};

  const proposals = coerceStatBlock(root.obligation_proposal_status_stats, proposalRowFromApi);
  proposals.items = sortObligationProposalChartRows(proposals.items);

  const implementation = coerceStatBlock(root.implementation_status_stats, implementationRowFromApi);
  implementation.items = sortImplementationChartRows(implementation.items);

  const notif = extractUnreadNotificationsFromOverview(root);

  return {
    totalUsers: Number(s.total_users) || 0,
    moderatorsCount: Number(s.total_moderators) || 0,
    activePosts: Number(s.active_notices) || 0,
    upcomingPosts: Number(s.upcoming_notices) || 0,
    ocdCount: Number(s.total_csos) || 0,
    institutionsCount: Number(s.total_institutions) || 0,
    proposalsByStatus: proposals.items,
    proposalsTotal: proposals.total,
    implementationByStatus: implementation.items,
    implementationTotal: implementation.total,
    unreadNotifications: notif.items,
    unreadNotificationsTotal: notif.total,
  };
}
